import { find, map } from 'lodash-es'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { getFestivalsMenu, IFestivalMenuItem } from '@/core/services/menu'
import { resetLogin } from '../../account/ducks/login'
import { logout } from '../../account/services/auth'
import { destroySession } from '../../account/services/session'
import {
  clear,
  saveFestivals,
  selectEdition,
  setFestivalName,
} from '../../app-reducer'
import { MenuItem, MenuItems } from '../../core/constants/menu'
import { useAppDispatch, useAppSelector } from '../../store'
import { LogoutConfirmationModal } from './LogoutConfirmationModal'
import { SidebarMenuItem } from './SidebarMenuItem'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui'
import { Icon } from './Icon'
import { EditionChange } from '@/core/context/EditionChangeContext'
import { cn } from '@/lib/utils'

interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
}

const Sidebar: FC<SidebarProps> = ({ collapsed = false }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const { editionChanged, setEditionChanged } = useContext(EditionChange)
  const [loading, setLoading] = useState(true)

  const { pathname } = useLocation()
  const { festivalName: routeFestivalName, editionId: routeEditionId } =
    useParams()

  const {
    festivals,
    user,
    activeFestival: activeFestivalFromRedux,
    activeEdition: activeEditionFromRedux,
  } = useAppSelector((s) => s.app)

  const getData = useCallback(async () => {
    if (!user) return
    try {
      const [festPromise] = await Promise.allSettled([getFestivalsMenu()])
      if (festPromise.status === 'fulfilled') {
        if (festPromise.value.status === 200) {
          const data = festPromise.value.data
          const storedFestivals = localStorage.getItem('festivals')
          if (storedFestivals) {
            const parsedFestivals = JSON.parse(storedFestivals)
            const mergedFestivals = data.map((f) => {
              const savedFestival = find(parsedFestivals, { id: f.id })
              if (savedFestival) {
                const storedEdition = find(savedFestival.editions, {
                  active: true,
                })
                const activeEditionFromData = find(f.editions, { active: true })
                if (
                  !!activeEditionFromData &&
                  storedEdition.id !== activeEditionFromData?.id
                ) {
                  for (const edition of f.editions) {
                    edition.active = activeEditionFromData.id === edition.id
                  }
                } else if (storedEdition) {
                  const selectedEdition = find(f.editions, {
                    id: storedEdition.id,
                  })
                  if (selectedEdition) {
                    for (const edition of f.editions) {
                      edition.active = selectedEdition.id === edition.id
                    }
                  }
                }
              }
              return f
            })

            dispatch(
              saveFestivals({
                festivals: mergedFestivals,
              }),
            )
          } else {
            dispatch(saveFestivals({ festivals: data }))
          }
        }
      }
    } catch (err) {
      console.log(err)
      navigate('/account/login', {
        replace: true,
      })
    } finally {
      setLoading(false)
    }
  }, [dispatch, navigate, user])

  useEffect(() => {
    if (editionChanged) {
      getData()
      setEditionChanged(false)
    }
  }, [editionChanged])

  useEffect(() => {
    getData()
  }, [getData])

  const onLogout = async () => {
    setLoggingOut(true)
    try {
      const { status } = await logout()
      if (status === 200) {
        setConfirmLogout(false)
        navigate('/account/login', { replace: true })
        destroySession()
        dispatch(clear())
        dispatch(resetLogin())
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoggingOut(false)
    }
  }

  const filterFestivals: IFestivalMenuItem[] = useMemo(() => {
    return festivals.filter((one) => {
      if (user?.superAdmin) return true
      return user?.festivalsPermissions.some(
        (o) => o.festivalName === one.platformName,
      )
    })
  }, [user?.festivalsPermissions, festivals, user?.superAdmin])

  const activeFestival = find(filterFestivals, {
    platformName: routeFestivalName || activeFestivalFromRedux?.platformName,
  })

  // Get filtered menu items for the active festival only
  const filteredMenuItems = useMemo(() => {
    if (!activeFestival) return []

    const festival = user?.festivalsPermissions.find(
      (o) => o.festivalName === activeFestival.platformName,
    )

    return MenuItems.map((one: MenuItem) => {
      // Hardcoded user restrictions
      if (
        (user?.email === 'cosmin@gmail.com' ||
          user?.email === 'kinder@gmail.com') &&
        one.type !== 'GENERATE_TICKETS'
      ) {
        return null
      } else if (
        user?.email === 'sasa@gmail.com' ||
        user?.email === 'barleamihai@gmail.com'
      ) {
        return null
      }

      const permission = one.type
        ? festival?.permissions.find((o) => o.type === one.type)
        : festival?.permissions.find((o) => o.type === one?.children?.[0]?.type)

      if (permission || user?.superAdmin) {
        const children = one.children?.map((child) => {
          const childPermission = festival?.permissions.find(
            (o) => o.type === child.type,
          )

          const nestedChildren = child.children?.map((nestedChild) => {
            const nestedChildPermission = festival?.permissions.find(
              (o) => o.type === nestedChild.type,
            )
            if (user?.superAdmin) {
              return {
                ...nestedChild,
                view: user?.superAdmin,
                edit: user?.superAdmin,
              }
            }

            if (nestedChildPermission) {
              return {
                ...nestedChild,
                view: nestedChildPermission.view,
                edit: nestedChildPermission.edit,
              }
            }

            return nestedChild
          }, [])

          const nestedWithView = nestedChildren?.some((o) => o.view === true)

          return {
            ...child,
            children: nestedChildren,
            view:
              user?.superAdmin || nestedWithView
                ? true
                : childPermission?.view || false,
            edit: user?.superAdmin || childPermission?.edit || false,
          }
        }, [])

        return {
          ...one,
          children: children,
          view:
            user?.superAdmin || (children?.length || 0) === 0
              ? permission?.view
              : (children?.filter((c) => c.view === true).length || 0) > 0,
          edit: user?.superAdmin || permission?.edit,
          festivalItem: activeFestival,
          festivalName: activeFestival.platformName,
          editionId:
            find(activeFestival.editions, { active: true })?.id ||
            activeFestival?.editions?.[0]?.id,
        }
      }

      return null
    }).filter((item) => item !== null && item.view !== false) as MenuItem[]
  }, [activeFestival, routeFestivalName, user])

  const activeEdition = useMemo(() => {
    if (!activeFestival) return undefined
    if (routeEditionId) {
      return find(activeFestival.editions, {
        id: routeEditionId || activeEditionFromRedux?.id,
      })
    }
    // Fallback: find active edition from Redux
    return (
      find(activeFestival.editions, { active: true }) ||
      activeFestival.editions[0]
    )
  }, [routeEditionId, activeFestival, activeEditionFromRedux])

  return (
    <div
      data-sidebar-container
      className={`fixed left-0 top-24 bottom-4 z-50 flex flex-col px-5 transition-transform duration-300 ease-in-out ${
        collapsed ? '-translate-x-full' : 'translate-x-0'
      }`}
    >
      {/* Main Sidebar Container */}
      <div className="w-[375px] backdrop-blur-sm bg-black/60 border-2 border-monochrome-120 rounded-[30px] flex flex-col pb-5 max-h-full relative z-20">
        <div
          className="flex-1 flex flex-col w-full items-center gap-2 overflow-y-auto overflow-x-hidden sidebar-menu-list min-h-0 px-5"
          style={{ scrollbarWidth: 'thin' }}
        >
          {/* Festival Selector - center top */}
          {filterFestivals.length > 0 && (
            <div className="flex items-center gap-2.5 w-full mt-5">
              <div className="flex flex-1 items-center gap-2.5 min-w-0 flex-col">
                <div className="flex-1 min-w-0 w-full">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        className="flex w-full items-center gap-2.5 data-[state=open]:bg-gradient-6 border border-monochrome-1/20 [&_svg]:transition-transform [&_svg]:duration-200 data-[state=open]:[&_svg]:rotate-180 justify-between"
                      >
                        <span className="text-white font-semibold text-button-m leading-8 truncate">
                          {activeFestival?.name || 'Select Festival'}
                        </span>
                        <Icon
                          icon="arrow-down"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-white shrink-0"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="min-w-[300px] max-h-[400px] overflow-y-auto p-5 py-0 divide-y divide-white/10">
                      {map(filterFestivals, (festival, index) => {
                        const firstActiveEdition = find(festival.editions, {
                          active: true,
                        })
                        const editionToSelect =
                          firstActiveEdition || festival.editions[0]

                        return (
                          <div key={`fest-${index}`}>
                            <DropdownMenuItem
                              className={cn(
                                '!text-button-m text-monochrome-1 pb-[15px] hover:opacity-70 focus:opacity-70 hover:bg-transparent focus:bg-transparent rounded-none px-0',
                                activeFestival?.platformName ===
                                  festival.platformName && 'text-accent-1',
                              )}
                              onClick={() => {
                                navigate(
                                  pathname
                                    .replace(
                                      routeFestivalName!,
                                      festival.platformName,
                                    )
                                    .replace(
                                      routeEditionId!,
                                      editionToSelect?.id || '',
                                    ),
                                )
                                dispatch(
                                  setFestivalName({
                                    festivalName: festival.platformName,
                                  }),
                                )
                                dispatch(
                                  selectEdition({
                                    edition: editionToSelect?.id || '',
                                    festival,
                                  }),
                                )
                              }}
                            >
                              {festival.name}
                            </DropdownMenuItem>
                          </div>
                        )
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="flex-1 min-w-0 w-full">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="secondary"
                        className="flex w-full items-center gap-2.5 data-[state=open]:bg-gradient-6 border border-monochrome-1/20 [&_svg]:transition-transform [&_svg]:duration-200 data-[state=open]:[&_svg]:rotate-180 justify-between"
                      >
                        <span className="text-white font-bold text-button-m truncate">
                          {activeEdition?.name || 'Select Edition'}
                        </span>
                        <Icon
                          icon="arrow-down"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          className="text-white shrink-0"
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    {activeFestival && (
                      <DropdownMenuContent className="min-w-[300px] p-5 py-0 divide-y divide-white/10">
                        {map(activeFestival.editions, (edition) => (
                          <DropdownMenuItem
                            key={edition.id}
                            onClick={() => {
                              navigate(
                                pathname.replace(routeEditionId!, edition.id),
                              )
                              dispatch(
                                selectEdition({
                                  edition: edition.id,
                                  festival: activeFestival,
                                }),
                              )
                            }}
                            className={cn(
                              '!text-button-m text-monochrome-1 pb-[15px] hover:opacity-70 focus:opacity-70 hover:bg-transparent focus:bg-transparent rounded-none px-0',
                              activeEdition?.id === edition.id &&
                                'text-accent-1',
                            )}
                          >
                            {edition.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    )}
                  </DropdownMenu>
                </div>
              </div>
              {/*<Button*/}
              {/*  variant="secondary"*/}
              {/*  className="w-[44px] h-[44px] shrink-0 rounded-full border border-monochrome-120"*/}
              {/*  onClick={() => {*/}
              {/*    navigate('/settings/festivals/New Festival')*/}
              {/*  }}*/}
              {/*>*/}
              {/*  <Icon*/}
              {/*    icon="plus"*/}
              {/*    width="18"*/}
              {/*    height="18"*/}
              {/*    viewBox="0 0 18 18"*/}
              {/*    fill="none"*/}
              {/*    className="text-white shrink-0"*/}
              {/*  />*/}
              {/*</Button>*/}
            </div>
          )}
          {/* Dashboard */}
          <SidebarMenuItem
            item={{
              title: 'Home',
              path: `/${activeFestival?.platformName}/${activeEdition?.id}`,
              children: [],
            }}
          />

          {/* Festival Menu Items - Only for selected festival */}
          {filteredMenuItems.map((item, index) => (
            <SidebarMenuItem key={`menu-${index}`} item={item} />
          ))}

          {/* Logout at bottom */}
          {/* <SidebarMenuItem
              item={{
                title: 'Logout',
                path: '/logout',
              }}
              onDropdownOpen={onDropdownOpen}
              onDropdownClose={onDropdownClose}
              onClick={() => setConfirmLogout(true)}
            /> */}
        </div>
      </div>

      <LogoutConfirmationModal
        setLogoutConfirmationModal={setConfirmLogout}
        showLogoutConfirmationModal={confirmLogout}
        onSuccess={onLogout}
        loading={loggingOut}
      />
    </div>
  )
}

export default Sidebar
